import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Input, Modal, Spin } from "antd";
import {
  ApartmentOutlined,
  CheckSquareOutlined,
  InboxOutlined,
  ProjectOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { OrganizationApi, ProjectApi, UserApi } from "../../services";
import { hasPermission } from "../../utils/helpers";
import { useDebounce, useModules } from "../../hooks";
import UserAvatar from "../UserAvatar";
import { COMMANDS } from "./commands";
import styles from "./styles.module.css";

/** Below this the palette shows commands only; two characters is where a
 *  server-side search stops matching everything. */
const MIN_QUERY = 2;

const PER_SECTION = 6;

/**
 * One search for the whole panel, on Ctrl/Cmd+K.
 *
 * The product already had a good task search — subtasks included, arrow keys,
 * opens the task — but it lived inside the board and searched one project, so
 * "which project was قرارداد بیمه in?" had no answer except opening projects
 * one at a time. This is that search with the project constraint removed, plus
 * the two other things people look for by name (projects and colleagues) and
 * the handful of places they navigate to over and over.
 *
 * Results are grouped, but keyboard navigation runs over one flat list, because
 * arrowing down should cross a group boundary without anyone thinking about it.
 */
const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();

  const { has: hasModule } = useModules();

  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useDebounce();
  const [activeIndex, setActiveIndex] = useState(0);

  const listRef = useRef(null);

  const query = debounced.trim();
  const searching = query.length >= MIN_QUERY;

  /* The wider task scope is the same one the organization-wide task list uses;
   * the API falls back to the user's own tasks when they may not have it. */
  const orgWide = useMemo(
    () =>
      hasPermission("manage_tasks") ||
      hasPermission("manage_projects") ||
      hasPermission("manage_org"),
    [],
  );

  const { data: tasks, isFetching: tasksFetching } = useQuery({
    queryKey: ["[Palette] Tasks", query, orgWide],
    queryFn: () =>
      OrganizationApi.searchTasks({
        params: {
          search: query,
          per_page: PER_SECTION,
          ...(orgWide ? { scope: "org" } : {}),
        },
      }).then((res) => res.data.tasks),
    enabled: open && searching,
    initialData: () => [],
    keepPreviousData: true,
    cacheTime: 0,
  });

  const { data: projects, isFetching: projectsFetching } = useQuery({
    queryKey: ["[Palette] Projects", query],
    queryFn: () =>
      ProjectApi.getProjects({
        params: { search: query, per_page: PER_SECTION },
      }).then((res) => res.data.projects),
    enabled: open && searching,
    initialData: () => [],
    keepPreviousData: true,
    cacheTime: 0,
  });

  const { data: people, isFetching: peopleFetching } = useQuery({
    queryKey: ["[Palette] People", query],
    queryFn: () =>
      UserApi.getUsers({
        params: { search: query, per_page: PER_SECTION },
      }).then((res) => res.data.users),
    enabled: open && searching && hasPermission("manage_users"),
    initialData: () => [],
    keepPreviousData: true,
    cacheTime: 0,
  });

  const isFetching = tasksFetching || projectsFetching || peopleFetching;

  const commands = useMemo(() => {
    const allowed = COMMANDS.filter(
      (item) =>
        (!item.permission || hasPermission(item.permission)) &&
        (!item.module || hasModule(item.module)),
    );

    if (!query) return allowed;

    const needle = query.toLowerCase();
    return allowed.filter(
      (item) =>
        item.label.toLowerCase().includes(needle) ||
        item.keywords?.toLowerCase().includes(needle),
    );
  }, [query, hasModule]);

  /* Groups in the order people look for things: what they can do, then the work
   * itself, then where it lives, then who is on it. */
  const groups = useMemo(() => {
    const built = [];

    if (commands.length) {
      built.push({
        key: "commands",
        title: "دستورها",
        items: commands.map((item) => ({
          id: `cmd-${item.key}`,
          kind: "command",
          data: item,
          to: item.to,
        })),
      });
    }

    if (searching) {
      if (tasks?.length) {
        built.push({
          key: "tasks",
          title: "تسک‌ها",
          items: tasks.map((task) => ({
            id: `task-${task.uuid}`,
            kind: "task",
            data: task,
            /* Opening the task on its own board rather than in a list: the
             * board reads `?task=` and the surrounding columns are the context
             * that answers "which project was this in". */
            to: task.project?.uuid
              ? `/projects/edit/${task.project.uuid}?task=${task.uuid}`
              : null,
          })),
        });
      }

      if (projects?.length) {
        built.push({
          key: "projects",
          title: "پروژه‌ها",
          items: projects.map((project) => ({
            id: `project-${project.uuid}`,
            kind: "project",
            data: project,
            to: `/projects/edit/${project.uuid}`,
          })),
        });
      }

      if (people?.length) {
        built.push({
          key: "people",
          title: "افراد",
          items: people.map((user) => ({
            id: `user-${user.uuid}`,
            kind: "user",
            data: user,
            to: `/users/edit/${user.uuid}`,
          })),
        });
      }
    }

    return built;
  }, [commands, searching, tasks, projects, people]);

  /* Grouped for the eye, flat for the keyboard. */
  const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, groups.length]);

  useEffect(() => {
    if (open) return;
    setTerm("");
    setDebounced("");
    setActiveIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    listRef.current
      ?.querySelectorAll("[data-palette-row]")
      ?.[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, flat]);

  const go = (entry) => {
    if (!entry?.to) return;
    navigate(entry.to);
    onClose();
  };

  const onKeyDown = (event) => {
    if (!flat.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(flat[activeIndex]);
    }
  };

  const renderRow = (entry, index) => {
    const active = index === activeIndex;
    const { kind, data } = entry;

    return (
      <button
        key={entry.id}
        type="button"
        data-palette-row
        className={`${styles.row} ${active ? styles.rowActive : ""}`}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => go(entry)}
      >
        <span className={styles.rowIcon}>
          {kind === "command" ? (
            data.icon
          ) : kind === "task" ? (
            data.parent_id ? (
              <ApartmentOutlined />
            ) : (
              <CheckSquareOutlined />
            )
          ) : kind === "project" ? (
            <ProjectOutlined />
          ) : (
            <UserAvatar user={data} size={22} />
          )}
        </span>

        <span className={styles.rowBody}>
          <span className={styles.rowTitle}>
            {kind === "user"
              ? `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
                data.mobile
              : data.title || data.label}
          </span>

          <span className={styles.rowMeta}>
            {kind === "task" && data.parent_id ? (
              <span className={styles.badge}>زیرتسک</span>
            ) : null}
            {kind === "task" && data.project?.title ? (
              <span className={styles.projectChip}>
                <ProjectOutlined />
                {data.project.title}
              </span>
            ) : null}
            {kind === "task" && data.step ? (
              <span
                className={styles.stepChip}
                style={{
                  color: data.step.color_code || "var(--ds-text-secondary)",
                  background: `${data.step.color_code || "#94a3b8"}1f`,
                }}
              >
                {data.step.title}
              </span>
            ) : null}
            {kind === "user" ? (
              <span className={styles.muted}>
                {data.roles?.map((role) => role.display_name).join("، ") ||
                  data.mobile}
              </span>
            ) : null}
            {kind === "command" ? (
              <span className={styles.muted}>رفتن به این بخش</span>
            ) : null}
          </span>
        </span>
      </button>
    );
  };

  const body = () => {
    if (!flat.length) {
      return (
        <div className={styles.state}>
          {isFetching ? (
            <Spin />
          ) : (
            <>
              <InboxOutlined className={styles.stateIcon} />
              <span>
                {searching
                  ? "چیزی با این عنوان پیدا نشد"
                  : "برای جست‌وجو در تسک‌ها، پروژه‌ها و افراد بنویس"}
              </span>
            </>
          )}
        </div>
      );
    }

    let cursor = -1;

    return (
      <div className={styles.results} ref={listRef}>
        {groups.map((group) => (
          <div className={styles.group} key={group.key}>
            <span className={styles.groupTitle}>
              {group.key === "commands" ? <ThunderboltOutlined /> : null}
              {group.title}
            </span>
            {group.items.map((entry) => {
              cursor += 1;
              return renderRow(entry, cursor);
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      className={`ds-modal ${styles.modal}`}
      closable={false}
      destroyOnHidden
    >
      <div className={styles.searchBox}>
        <Input
          size="large"
          autoFocus
          allowClear
          variant="borderless"
          prefix={<SearchOutlined className={styles.searchIcon} />}
          placeholder="تسک، پروژه، کاربر یا یک دستور..."
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setDebounced(event.target.value);
          }}
          onKeyDown={onKeyDown}
          suffix={isFetching && searching ? <Spin size="small" /> : null}
        />
      </div>

      {body()}

      <div className={styles.footer}>
        <span className={styles.hint}>
          <span className={styles.kbd}>↑</span>
          <span className={styles.kbd}>↓</span>
          برای جابه‌جایی و
          <span className={styles.kbd}>Enter</span>
          برای باز کردن
        </span>
        <span className={styles.hint}>
          <span className={styles.kbd}>Esc</span>
          برای بستن
        </span>
      </div>
    </Modal>
  );
};

export default CommandPalette;
